/**
 * CMS Host Queries
 *
 * Server-side queries for fetching host data.
 */

import { query } from "@solidjs/router";
import type { CMSHost, CMSHostResponse } from "~/types/cms";
import { env } from "~/config/env";
import { logger } from "~/lib/logger/logger";
import { fetchFromCMS, findByTenant } from "../queryUtils";
import { buildHostBySlugUrl, buildHostsListUrl } from "../queryUrls";

/**
 * Server-side query for fetching a host by slug
 *
 * @param slug - The host slug (e.g., 'lisa-mueller')
 * @param tenantSlug - The tenant slug for multi-tenancy filtering
 * @returns Host data or null if not found
 */
export const getHostBySlugQuery = query(async (slug: string, tenantSlug: string) => {
  "use server";

  const LOG_PREFIX = "[hostBySlug]";

  // URL built via shared queryUrls module (also used by performance audit)
  const url = `${env.cmsApiUrl}${buildHostBySlugUrl({ slug, tenantSlug })}`;

  const data = await fetchFromCMS<CMSHostResponse>(url, { logPrefix: LOG_PREFIX });

  if (!data?.docs || data.docs.length === 0) {
    logger.warn(`${LOG_PREFIX} No host found with slug: ${slug}`);
    return null;
  }

  // Payload ignores nested tenant[slug] filters — filter client-side
  const host = findByTenant(data.docs, tenantSlug);

  if (!host) {
    logger.warn(`${LOG_PREFIX} No host found for tenant: ${tenantSlug}`);
    return null;
  }

  logger.info(`${LOG_PREFIX} Found host: ${host.name}`);
  return host as CMSHost;
}, "hostBySlug");

/**
 * Server-side query for fetching all hosts
 *
 * @param tenantSlug - The tenant slug for multi-tenancy filtering
 * @returns Array of hosts or empty array if none found
 */
export const getAllHostsQuery = query(async (tenantSlug: string) => {
  "use server";

  const LOG_PREFIX = "[allHosts]";

  // URL built via shared queryUrls module (also used by performance audit)
  const url = `${env.cmsApiUrl}${buildHostsListUrl({ tenantSlug })}`;

  const data = await fetchFromCMS<CMSHostResponse>(url, { logPrefix: LOG_PREFIX });

  if (!data?.docs || data.docs.length === 0) {
    logger.warn(`${LOG_PREFIX} No hosts found for tenant: ${tenantSlug}`);
    return [];
  }

  logger.info(`${LOG_PREFIX} Found ${data.docs.length} hosts`);
  return data.docs as CMSHost[];
}, "allHosts");
